// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — SportsBook + Fantasy Leagues
// Sports betting & fantasy league hosting on $FORGE
// ================================================================
import React, { useState } from 'react';
import { useCasino } from '../context/CasinoShell';

// Demo sports data — will be live API feeds in production
const FEATURED_EVENTS = [
  {
    id: 'nhl-1',
    sport: 'Hockey',
    icon: '🏒',
    league: 'NHL',
    home: 'Edmonton Oilers',
    away: 'Calgary Flames',
    homeOdds: -150,
    awayOdds: +130,
    drawOdds: null,
    time: 'Tonight 8:00 PM MT',
    status: 'upcoming',
  },
  {
    id: 'nfl-1',
    sport: 'Football',
    icon: '🏈',
    league: 'NFL',
    home: 'Kansas City Chiefs',
    away: 'Buffalo Bills',
    homeOdds: -120,
    awayOdds: +100,
    drawOdds: null,
    time: 'Sunday 4:25 PM ET',
    status: 'upcoming',
  },
  {
    id: 'nba-1',
    sport: 'Basketball',
    icon: '🏀',
    league: 'NBA',
    home: 'LA Lakers',
    away: 'Golden State Warriors',
    homeOdds: +110,
    awayOdds: -130,
    drawOdds: null,
    time: 'Tomorrow 7:30 PM PT',
    status: 'upcoming',
  },
  {
    id: 'soccer-1',
    sport: 'Soccer',
    icon: '⚽',
    league: 'Premier League',
    home: 'Manchester City',
    away: 'Arsenal',
    homeOdds: -110,
    awayOdds: +250,
    drawOdds: +220,
    time: 'Saturday 12:30 PM ET',
    status: 'upcoming',
  },
  {
    id: 'ufc-1',
    sport: 'MMA',
    icon: '🥊',
    league: 'UFC',
    home: 'Fighter A',
    away: 'Fighter B',
    homeOdds: -200,
    awayOdds: +170,
    drawOdds: null,
    time: 'Saturday 10:00 PM ET',
    status: 'upcoming',
  },
];

const FANTASY_LEAGUES = [
  {
    id: 'nhl-fantasy',
    name: 'Forge Hockey League',
    sport: '🏒 NHL',
    entryFee: 25,
    prizePool: 500,
    players: '18/20',
    status: 'Filling',
    type: 'Weekly',
  },
  {
    id: 'nfl-fantasy',
    name: 'Forge Football League',
    sport: '🏈 NFL',
    entryFee: 50,
    prizePool: 1000,
    players: '8/12',
    status: 'Filling',
    type: 'Season',
  },
  {
    id: 'nba-fantasy',
    name: 'Forge Basketball League',
    sport: '🏀 NBA',
    entryFee: 10,
    prizePool: 200,
    players: '12/16',
    status: 'Filling',
    type: 'Daily',
  },
];

const SPORTS_NAV = ['Featured', 'Hockey', 'Football', 'Basketball', 'Soccer', 'MMA', 'Fantasy'];

export default function SportsBook() {
  const { forgeBalance } = useCasino();
  const [activeTab, setActiveTab] = useState('Featured');
  const [selectedBets, setSelectedBets] = useState([]);

  const formatOdds = (odds) => {
    return odds > 0 ? `+${odds}` : String(odds);
  };

  const toggleBet = (eventId, selection, odds) => {
    setSelectedBets(prev => {
      const exists = prev.find(b => b.eventId === eventId && b.selection === selection);
      if (exists) return prev.filter(b => !(b.eventId === eventId && b.selection === selection));
      return [...prev.filter(b => b.eventId !== eventId), { eventId, selection, odds }];
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🏆 SPORTSBOOK</h1>
        <div style={styles.balanceBadge}>
          <span style={styles.balLabel}>BALANCE</span>
          <span style={styles.balValue}>${forgeBalance.toFixed(2)}</span>
        </div>
      </div>

      {/* Sport Navigation */}
      <div style={styles.sportNav}>
        {SPORTS_NAV.map(sport => (
          <button
            key={sport}
            style={{ ...styles.sportBtn, background: activeTab === sport ? '#DC2626' : '#1a1a1a', color: activeTab === sport ? '#fff' : '#888' }}
            onClick={() => setActiveTab(sport)}
          >{sport}</button>
        ))}
      </div>

      {activeTab !== 'Fantasy' ? (
        <>
          {/* Events */}
          <div style={styles.events}>
            {FEATURED_EVENTS.filter(e => activeTab === 'Featured' || e.sport === activeTab).map(event => (
              <div key={event.id} style={styles.eventCard}>
                <div style={styles.eventHeader}>
                  <span style={styles.eventLeague}>{event.icon} {event.league}</span>
                  <span style={styles.eventTime}>{event.time}</span>
                </div>
                <div style={styles.matchup}>
                  <button
                    style={{
                      ...styles.teamBtn,
                      borderColor: selectedBets.some(b => b.eventId === event.id && b.selection === 'home') ? '#DC2626' : '#333',
                      background: selectedBets.some(b => b.eventId === event.id && b.selection === 'home') ? '#DC262233' : '#111',
                    }}
                    onClick={() => toggleBet(event.id, 'home', event.homeOdds)}
                  >
                    <span style={styles.teamName}>{event.home}</span>
                    <span style={{ ...styles.odds, color: event.homeOdds < 0 ? '#DC2626' : '#00FF88' }}>
                      {formatOdds(event.homeOdds)}
                    </span>
                  </button>

                  {event.drawOdds && (
                    <button
                      style={{
                        ...styles.drawBtn,
                        borderColor: selectedBets.some(b => b.eventId === event.id && b.selection === 'draw') ? '#FFD700' : '#333',
                      }}
                      onClick={() => toggleBet(event.id, 'draw', event.drawOdds)}
                    >
                      <span style={styles.drawLabel}>DRAW</span>
                      <span style={styles.drawOdds}>{formatOdds(event.drawOdds)}</span>
                    </button>
                  )}

                  <button
                    style={{
                      ...styles.teamBtn,
                      borderColor: selectedBets.some(b => b.eventId === event.id && b.selection === 'away') ? '#DC2626' : '#333',
                      background: selectedBets.some(b => b.eventId === event.id && b.selection === 'away') ? '#DC262233' : '#111',
                    }}
                    onClick={() => toggleBet(event.id, 'away', event.awayOdds)}
                  >
                    <span style={styles.teamName}>{event.away}</span>
                    <span style={{ ...styles.odds, color: event.awayOdds < 0 ? '#DC2626' : '#00FF88' }}>
                      {formatOdds(event.awayOdds)}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Bet Slip */}
          {selectedBets.length > 0 && (
            <div style={styles.betSlip}>
              <h3 style={styles.slipTitle}>BET SLIP ({selectedBets.length})</h3>
              {selectedBets.map((bet, i) => {
                const event = FEATURED_EVENTS.find(e => e.id === bet.eventId);
                return (
                  <div key={i} style={styles.slipRow}>
                    <span style={styles.slipTeam}>
                      {bet.selection === 'home' ? event?.home : bet.selection === 'away' ? event?.away : 'Draw'}
                    </span>
                    <span style={styles.slipOdds}>{formatOdds(bet.odds)}</span>
                  </div>
                );
              })}
              <div style={styles.slipNote}>
                Sports betting integration coming soon. All bets will be settled with $FORGE on-chain.
              </div>
            </div>
          )}
        </>
      ) : (
        /* Fantasy Leagues */
        <div style={styles.fantasySection}>
          <h2 style={styles.fantasyTitle}>FANTASY LEAGUES</h2>
          <p style={styles.fantasySubtitle}>Create or join leagues. Entry fees and prizes in $FORGE.</p>

          {FANTASY_LEAGUES.map(league => (
            <div key={league.id} style={styles.leagueCard}>
              <div style={styles.leagueHeader}>
                <h3 style={styles.leagueName}>{league.name}</h3>
                <span style={styles.leagueType}>{league.type}</span>
              </div>
              <div style={styles.leagueInfo}>
                <span>{league.sport}</span>
                <span>Entry: ${league.entryFee} $FORGE</span>
                <span>Prize: ${league.prizePool}</span>
                <span>{league.players}</span>
              </div>
              <div style={styles.leagueActions}>
                <button style={styles.joinBtn}>JOIN LEAGUE</button>
                <span style={{ ...styles.leagueStatus, color: league.status === 'Filling' ? '#00FF88' : '#FFD700' }}>
                  {league.status}
                </span>
              </div>
            </div>
          ))}

          <button style={styles.createBtn}>
            + CREATE NEW LEAGUE
          </button>
        </div>
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          All sports bets and fantasy leagues settled on $FORGE blockchain.
          Provably fair odds. Transparent payouts.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 700, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, margin: 0 },
  balanceBadge: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '6px 12px', background: '#111', borderRadius: 6, border: '1px solid #222' },
  balLabel: { fontSize: 7, color: '#555', fontWeight: 700 },
  balValue: { fontSize: 16, fontWeight: 900, color: '#FFD700', fontFamily: "'Courier New', monospace" },
  sportNav: { display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' },
  sportBtn: { padding: '6px 12px', borderRadius: 6, border: '1px solid #333', fontSize: 10, fontWeight: 700, cursor: 'pointer' },
  events: { display: 'flex', flexDirection: 'column', gap: 10 },
  eventCard: { padding: 14, background: '#111', borderRadius: 10, border: '1px solid #222' },
  eventHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: 10 },
  eventLeague: { fontSize: 11, color: '#DC2626', fontWeight: 700 },
  eventTime: { fontSize: 10, color: '#555' },
  matchup: { display: 'flex', gap: 6, alignItems: 'stretch' },
  teamBtn: {
    flex: 1, padding: '10px 8px', borderRadius: 8, border: '1px solid', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  teamName: { fontSize: 11, color: '#fff', fontWeight: 700, textAlign: 'center' },
  odds: { fontSize: 14, fontWeight: 900, fontFamily: "'Courier New', monospace" },
  drawBtn: {
    padding: '10px 12px', borderRadius: 8, border: '1px solid #333', background: '#111',
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  drawLabel: { fontSize: 8, color: '#666', fontWeight: 700 },
  drawOdds: { fontSize: 12, color: '#FFD700', fontWeight: 900 },
  betSlip: {
    marginTop: 16, padding: 14, background: '#111', borderRadius: 10,
    border: '2px solid #DC2626',
  },
  slipTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 10, marginTop: 0 },
  slipRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1a1a1a', fontSize: 11, color: '#ccc' },
  slipTeam: { fontWeight: 700 },
  slipOdds: { color: '#FFD700', fontWeight: 900 },
  slipNote: { fontSize: 10, color: '#555', marginTop: 10, textAlign: 'center' },
  fantasySection: {},
  fantasyTitle: { fontSize: 18, color: '#fff', fontWeight: 900, letterSpacing: 2, marginBottom: 4 },
  fantasySubtitle: { fontSize: 11, color: '#666', marginBottom: 16 },
  leagueCard: { padding: 14, background: '#111', borderRadius: 10, border: '1px solid #222', marginBottom: 10 },
  leagueHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  leagueName: { fontSize: 13, color: '#fff', fontWeight: 900, margin: 0 },
  leagueType: { fontSize: 9, color: '#DC2626', fontWeight: 700, padding: '2px 8px', background: '#DC262222', borderRadius: 4 },
  leagueInfo: { display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginBottom: 10 },
  leagueActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  joinBtn: {
    padding: '8px 20px', borderRadius: 6, border: '1px solid #DC2626', background: '#DC262233',
    color: '#DC2626', fontSize: 10, fontWeight: 900, cursor: 'pointer',
  },
  leagueStatus: { fontSize: 10, fontWeight: 700 },
  createBtn: {
    width: '100%', padding: 12, borderRadius: 8, border: '2px dashed #333',
    background: 'none', color: '#666', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 10,
  },
  footer: { textAlign: 'center', padding: '20px 0', marginTop: 20 },
  footerText: { fontSize: 10, color: '#444', lineHeight: 1.5 },
};
